import { Box, Typography, Button, Grid, Divider, Accordion, AccordionDetails, AccordionSummary } from '@mui/material'
import { FC, useCallback, useEffect, useState } from 'react'
import DraggableList from 'src/components/DraggableList'
import MasterWorkoutExerciseViewModel from 'src/viewmodels/MasterWorkoutExerciseViewModel'
import MasterWorkoutExerciseDaysItem from './MasterWorkoutExerciseDaysItem'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'

interface MasterWorkoutExerciseDaysListProps {
	items: Map<string, MasterWorkoutExerciseViewModel[]>
	days: number[]
	onCreateMWE: (day: number, displayOrder: number) => void
	onEditMWE: (vm: MasterWorkoutExerciseViewModel) => void
	onOrderChange: (items: MasterWorkoutExerciseViewModel[]) => void
	canEdit: boolean
	activeMasterWorkoutExercise?: MasterWorkoutExerciseViewModel
}

const MasterWorkoutExerciseDaysList: FC<MasterWorkoutExerciseDaysListProps> = ({
	items,
	days,
	onCreateMWE,
	onEditMWE,
	onOrderChange,
	canEdit,
	activeMasterWorkoutExercise
}) => {
	const [selected, setSelected] = useState<{ week: number; day: number; displayOrder: number }>()
	const [expanded, setExpanded] = useState<boolean[]>(days.map(() => true))

	useEffect(() => {
		if (activeMasterWorkoutExercise?.masterWorkoutExercise) {
			const { week, day, displayOrder } = activeMasterWorkoutExercise.masterWorkoutExercise
			setSelected({ week, day, displayOrder })
		}
	}, [activeMasterWorkoutExercise])

	const handleSelect = useCallback((vm: MasterWorkoutExerciseViewModel) => {
		setSelected({ week: vm.masterWorkoutExercise.week, day: vm.masterWorkoutExercise.day, displayOrder: vm.masterWorkoutExercise.displayOrder })
		onEditMWE(vm)
	}, [])

	const handleOrderChange = useCallback((items: MasterWorkoutExerciseViewModel[]) => {
		setSelected(null)
		onOrderChange(items)
	},[])

	useEffect(() => {
		setExpanded(days.map(() => true))
	}, [days])

	const handleAccordionChange = (idx: number) => {
		const changeExpanded = [...expanded]
		changeExpanded[idx] = !changeExpanded[idx]
		setExpanded([...changeExpanded])
	}

	return (
		<Grid container>
			{days.map((day, idx) => {
				const values = items.get(`${day}`)
				return (
					<Grid key={day} item xs={12}>
						<Accordion key={day} expanded={expanded[idx] || false}>
							<AccordionSummary expandIcon={<ExpandMoreIcon onClick={() => handleAccordionChange(idx)} />}>
								<Box display="flex" flex="auto">
									<Typography variant="h4" sx={{ flexGrow: 1 }}>
										Day {day}
									</Typography>
								</Box>
								{canEdit && (
									<Button
										sx={{ mx: 1 }}
										variant="outlined"
										onClick={(e: any) => {
											onCreateMWE(day, values?.length || 0)
											e.preventDefault()
										}}>
										Create
									</Button>
								)}
							</AccordionSummary>
							<AccordionDetails>
								{values?.length > 0 && (
									<DraggableList
										id={`day-${day}`}
										key={day}
										items={values}
										onOrderChange={handleOrderChange}
										disableDrag={!canEdit}
										renderItem={(vm: MasterWorkoutExerciseViewModel) => {
											const isSelected =
												selected &&
												selected.week === vm.masterWorkoutExercise.week &&
												selected.day === vm.masterWorkoutExercise.day &&
												selected.displayOrder === vm.masterWorkoutExercise.displayOrder
											return <MasterWorkoutExerciseDaysItem onSelect={handleSelect} canEdit={canEdit} viewModel={vm} selected={isSelected} />
										}}
									/>
								)}
							</AccordionDetails>
						</Accordion>
					</Grid>
				)
			})}
		</Grid>
	)
}

export default MasterWorkoutExerciseDaysList
